import {
  Sidebar,
  SidebarDivider,
  SidebarExpandButton,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
} from '@backstage/core-components';
import { FeatureFlagged } from '@backstage/core-app-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ListAltIcon from '@material-ui/icons/ListAlt';
import GroupIcon from '@material-ui/icons/Group';
import RepositoryIcon from '@material-ui/icons/Storage';
import CloudIcon from '@material-ui/icons/Cloud';
import BugReportIcon from '@material-ui/icons/BugReport';
import DnsIcon from '@material-ui/icons/Dns';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import HomeIcon from '@material-ui/icons/Home';
import MenuIcon from '@material-ui/icons/Menu';
import PersonIcon from '@material-ui/icons/Person';
import SearchIcon from '@material-ui/icons/Search';
import SettingsIcon from '@material-ui/icons/Settings';
import AppsIcon from '@material-ui/icons/Apps';
import TimelineIcon from '@material-ui/icons/Timeline';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { Box, makeStyles } from '@material-ui/core';
import { SidebarAccordionGroup } from '../../Components';

sidebarConfig.drawerWidthOpen = 240;

const useBottomRowStyles = makeStyles(theme => ({
  border: {
    borderTop: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(0,0,0,0.12)'
    }`,
    width: '100%',
  },
  rowExpanded: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  rowCollapsed: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  contentExpanded: {
    flex: 1,
    overflow: 'hidden',
  },
  expandWrapper: {
    flexShrink: 0,
    width: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const BottomControls = () => {
  const classes = useBottomRowStyles();
  const { isOpen } = useSidebarOpenState();

  const SettingsItem = (
    <SidebarItem icon={SettingsIcon} to="/settings" text="Settings" />
  );

  return (
    <Box className={classes.border}>
      {isOpen ? (
        <Box className={classes.rowExpanded}>
          <Box className={classes.contentExpanded}>
            <NotificationsSidebarItem />
            {SettingsItem}
          </Box>
          <Box className={classes.expandWrapper}>
            <SidebarExpandButton />
          </Box>
        </Box>
      ) : (
        <Box className={classes.rowCollapsed}>
          <SidebarExpandButton />
          <NotificationsSidebarItem />
          {SettingsItem}
        </Box>
      )}
    </Box>
  );
};

export const SidebarContent: ReturnType<typeof NavContentBlueprint.make> =
  NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      navItems.take('page:catalog');
      navItems.take('page:app/catalog-groups');
      navItems.take('page:app/catalog-users');
      navItems.take('page:app/catalog-repositories');
      navItems.take('page:app/catalog-subscriptions');
      navItems.take('page:app/catalog-kubernetes-clusters');
      navItems.take('page:app/catalog-jira-teams');
      navItems.take('page:app/catalog-pagerduty-teams');
      navItems.take('page:app/catalog-kaap-tenants');
      const catalogBaseHref = '/catalog';

      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Skipped items
      nav.take('page:home/home'); // Home is rendered explicitly below Search
      nav.take('page:home'); // Fallback in case extension id differs
      nav.take('page:search'); // Using search modal instead
      nav.take('page:user-settings'); // Using dedicated bottom settings item

      // Taken so scorecards and all-scorecards don't also appear in nav.rest()
      nav.take('page:scorecard-frontend/scorecards');
      nav.take('page:scorecard-frontend/all-scorecards');
      nav.take('page:engineering-insights-frontend/engineering-insights');

      // Reached from the Engineering Toolkit card on the Home page instead
      nav.take('page:secrets-sharing-frontend/secrets-sharing');

      return (
        <Sidebar disableExpandOnHover>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarAccordionGroup icon={<GroupIcon />} label="Catalog">
              <SidebarItem
                icon={AppsIcon}
                to={catalogBaseHref}
                text="All Catalogs"
              />
              <SidebarItem
                icon={GroupIcon}
                to="/catalog/groups"
                text="Teams"
              />
              <SidebarItem
                icon={PersonIcon}
                to="/catalog/users"
                text="Users"
              />
              <SidebarItem
                icon={RepositoryIcon}
                to="/catalog/repositories"
                text="Repositories"
              />
              {nav.take('page:api-docs')}
              {nav.take('page:ai-tools-frontend/ai-tools')}
              <SidebarItem
                icon={CloudIcon}
                to="/catalog/subscriptions"
                text="Subscriptions"
              />
              <SidebarItem
                icon={DeviceHubIcon}
                to="/catalog/kubernetes-clusters"
                text="Kubernetes Clusters"
              />
              <SidebarItem
                icon={BugReportIcon}
                to="/catalog/jira-teams"
                text="Jira Teams"
              />
              <SidebarItem
                icon={NotificationsActiveIcon}
                to="/catalog/pagerduty-teams"
                text="PagerDuty Teams"
              />
              <SidebarItem
                icon={DnsIcon}
                to="/catalog/kaap-tenants"
                text="KaaP Tenants"
              />
            </SidebarAccordionGroup>
            <SidebarAccordionGroup icon={<TimelineIcon />} label="Dashboards">
              <SidebarItem
                icon={AssessmentIcon}
                to="/scorecards"
                text="Scorecards"
              />
              <SidebarItem
                icon={ListAltIcon}
                to="/all-scorecards"
                text="All Scorecards"
              />
              <SidebarItem
                icon={EqualizerIcon}
                to="/engineering-insights"
                text="Insights"
              />
              {nav.take('page:ai-readiness-frontend/ai-readiness')}
            </SidebarAccordionGroup>
            {nav.take('page:scaffolder')}
            {nav.take('page:tech-radar-frontend/tech-radar')}
            {nav.take('page:tomtom-lab-frontend/tomtom-lab')}
            {nav.take('page:ai-library-frontend/ai-library')}
            <FeatureFlagged with="developer-mode">
              {nav.take('page:devtools')}
            </FeatureFlagged>
            <SidebarDivider />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <FeatureFlagged with="developer-mode">
            {nav.take('page:app-visualizer')}
          </FeatureFlagged>
          <BottomControls />
        </Sidebar>
      );
    },
  },
  });
